<template>
  <div>
    <!-- EXISTING INVOICES FOR THIS CONTRACT -->
    <SectionWrapper v-if="invoices.length && wrapperIsNew" class="mt-2">
      <h3>
        {{ t('pages.operatingcostinvoices.headlineExistingInvoices') }}
      </h3>

      <DataTable
        data-key="operatingcostinvoice"
        :data="existingInvoicesForList"
        :columns="columns"
        :isLoadingFinished="isLoadingFinished"
        selectionMode="single"
        :selectedRows="selectedRowsForExistingList"
        :checkboxMultiselect="false"
        :tableActions="false"
        :showPrimaryToolbar="false"
        :showSecondaryToolbarButtons="false"
        :showSecondaryToolbar="false"
        :hasExpandedRow="true"
        :onRowClick="onRowClick"
      >
        <template #custom-expanded-row="{ row }">
          <OperatingCostInvoicesDetail
            v-if="row.id?.meta('rawvalue')"
            :uuid="row.id?.meta('rawvalue')"
            :isNew="false"
            :editMode="false"
            :inModal="true"
            :isSubComponent="isSubComponent"
            :ticket="ticket"
            :contracts="contractsProp"
            :latestInvoiceId="latestInvoiceIdForDetail"
            :latestInvoiceLineItems="latestInvoiceLineItemsForDetail"
          />
        </template>
      </DataTable>
    </SectionWrapper>

    <ModalOrPage v-if="props.existingListMode === 'modal'" ref="showInvoiceRef">
      <template #default="{ state }">
        <OperatingCostInvoicesDetail
          :uuid="state?.uuid ?? undefined"
          :isNew="false"
          :editMode="false"
          :inModal="true"
          :ticket="ticket"
          :isSubComponent="isSubComponent"
          :contracts="contractsProp"
          :latestInvoiceId="latestInvoiceIdForDetail"
          :latestInvoiceLineItems="latestInvoiceLineItemsForDetail"
        />
      </template>
    </ModalOrPage>

    <SectionWrapper v-if="utilityBills?.length" class="mt-2">
      <h3>
        {{ t('pages.operatingcostinvoices.headlineUtilitybills', { count: utilityBills.length }) }}
      </h3>
      <div
        v-for="(utilityBill, idx) in utilityBills"
        :key="utilityBill.documentId || `bill-${idx}`"
      >
        <SingleAccordion v-if="utilityBill?.documentId" :lazy="false" :title="utilityBill.name">
          <FileViewer
            :uuid="utilityBill.documentId"
            :mime="utilityBill.mimeType ?? 'application/pdf'"
            :filename="utilityBill.name"
          />
        </SingleAccordion>
      </div>
    </SectionWrapper>

    <!-- CREATE / EDIT -->
    <h2 v-if="inModal" class="mt-12">
      {{
        selectedInvoiceId
          ? t('pages.operatingcostinvoices.headlineUtilitybill', { count: invoices.length })
          : t('pages.operatingcostinvoices.headlineUNewUtilitybill', { count: invoices.length })
      }}
    </h2>

    <NotificationBar
      v-if="!selectedInvoiceId && existingInvoicesForList.length > 0 && templateSourceInvoice"
      class="mt-2"
      :mainText="
        t(notificationKey, {
          name: templateSourceInvoice?.issuerLegalName,
        })
      "
      :severity="!!isUsingLatestInvoiceAsTemplate ? 'info' : ''"
    />

    <!-- CREATE NEW INVOICE -->
    <OperatingCostInvoicesDetail
      v-if="!selectedInvoiceId"
      :key="'new-invoice'"
      :isNew="effectiveIsNew"
      :editMode="effectiveEditMode"
      :inModal="inModal"
      :isSubComponent="isSubComponent"
      :initialData="initialDataForCreate"
      :placeholders="placeholdersForDetail"
      :suggestions="suggestions"
      :ticket="ticket"
      :allDisabled="false"
      :disabledFields="disabledFieldsForDetail"
      :contracts="contractsProp"
      :latestInvoiceId="latestInvoiceIdForDetail"
      :latestInvoiceLineItems="latestInvoiceLineItemsForDetail"
      :importSourceInvoices="importSourceInvoices"
      @submitSuccess="onCreated"
      @contractIdChange="onContractSelected"
    />

    <!-- EDIT EXISTING INVOICE -->
    <OperatingCostInvoicesDetail
      v-else
      :uuid="selectedInvoiceId"
      :isNew="false"
      :editMode="effectiveEditMode"
      :inModal="inModal"
      :isSubComponent="isSubComponent"
      :contracts="contractsProp"
      :ticket="ticket"
      :latestInvoiceId="latestInvoiceIdForDetail"
      :latestInvoiceLineItems="latestInvoiceLineItemsForDetail"
      :importSourceInvoices="importSourceInvoices"
      @submitSuccess="onEdited"
      @contractIdChange="onContractSelected"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRaw, watch } from 'vue';
import type { DataTableRowClickEvent } from 'primevue/datatable';
import { useI18n } from 'vue-i18n';

import type { ModalOrPageExpose } from '@/components/details/detail-modal-or-page/DetailModalOrPage.vue';
import ModalOrPage from '@/components/details/detail-modal-or-page/DetailModalOrPage.vue';
import { buildPlaceholders } from '@/components/details/detailTypes';
import SectionWrapper from '@/components/section-wrapper/SectionWrapper.vue';
import type { ContractSimple } from '@/views/contracts/contractsTypes';
import { useContracts, useContractsConverter } from '@/views/contracts/hooks/useContracts';
import { useOperatingCostInvoiceLinesConverter } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/hooks/useOperatingCostInvoiceLines';
import type { OperatingCostInvoiceLineItemSimple } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';
import { columns } from '@/views/operatingcostinvoices/configs/operatingCostInvoiceColumnConfig';
import {
  useOperatingCostInvoices,
  useOperatingCostInvoicesConverter,
} from '@/views/operatingcostinvoices/hooks/useOperatingCostInvoices';
import OperatingCostInvoicesDetail from '@/views/operatingcostinvoices/OperatingcostinvoicesDetail.vue';
import type { OperatingCostInvoiceSimple } from '@/views/operatingcostinvoices/operatingCostInvoiceTypes';
import { buildFieldSuggestions } from '@/composables/useFieldSuggestions';
import { getNextPeriod, getSameDayNextYear } from '@/utils/dateUtils';
import type { ContractV1Dto, TicketDocumentV1Dto } from '@/openapi';

import type { TicketEnriched } from '../tickets/ticketsTypes';

const props = withDefaults(
  defineProps<{
    contractId?: string;
    invoiceId?: string | null;
    ticket?: TicketEnriched | null;
    isNew?: boolean;
    editMode?: boolean;
    inModal?: boolean;
    isSubComponent?: boolean;
    existingListMode?: 'inline' | 'modal';

    // utilityBills from ticket context (to prefill certain fields / suggestions)
    utilityBills?: TicketDocumentV1Dto[];

    // prefills/placeholders/flags overrides
    initialDataOverride?: Partial<OperatingCostInvoiceSimple>;
    placeholdersOverride?: Partial<Record<keyof OperatingCostInvoiceSimple, string>>;
    latestInvoiceIdOverride?: string | null;
    latestInvoiceLineItemsOverride?: OperatingCostInvoiceLineItemSimple[];
    disabledFieldsOverride?: Partial<Record<keyof OperatingCostInvoiceSimple, boolean>>;
  }>(),
  {
    contractId: undefined,
    invoiceId: null,
    ticket: null,
    isNew: undefined,
    editMode: undefined,
    inModal: false,
    isSubComponent: false,
    existingListMode: 'inline',

    utilityBills: undefined,
    initialDataOverride: undefined,
    placeholdersOverride: undefined,
    latestInvoiceIdOverride: undefined,
    latestInvoiceLineItemsOverride: undefined,
    disabledFieldsOverride: undefined,
  },
);

const emit = defineEmits<{
  (e: 'created', payload: { id?: string }): void;
  (e: 'edited', payload: { id?: string }): void;
  (e: 'contractIdChange', contractId: string | null): void;
}>();

const { t } = useI18n();

const { fetchContract } = useContracts();
const { convertContractDtoToContractSimple } = useContractsConverter();

const { fetchOperatingCostInvoice } = useOperatingCostInvoices();
const {
  convertOperatingCostInvoiceDtoListToOperatingCostInvoiceSimpleList,
  fetchAllAndConvertToSimple,
} = useOperatingCostInvoicesConverter();
const { convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList } =
  useOperatingCostInvoiceLinesConverter();

const isLoadingFinished = ref(false);
const showInvoiceRef = ref<ModalOrPageExpose | null>(null);
const importSourceInvoices = ref<OperatingCostInvoiceSimple[]>([]);

// ----- CONTRACT DATA ---------------------------------------------------------

const contractDto = ref<ContractV1Dto | null>(null);
const contractSimple = ref<ContractSimple | null>(null);

const selectedContractIdFromDetail = ref<string | null>(props.contractId ?? null);
const currentContractId = computed(() => {
  // Priority:
  // 1. passed from outside via prop
  // 2. else: selected in detail (e.g. when user changes contract in detail view)
  return props.contractId ?? selectedContractIdFromDetail.value ?? null;
});

const selectedRowsForExistingList = computed(() => {
  // Use latest invoice id as selected row, if available
  const id = latestInvoice.value?.id;
  return id ? [id] : [];
});

const isUsingLatestInvoiceAsTemplate = computed(() => {
  const templateId = templateSourceInvoice.value?.id;
  const latestId = latestInvoice.value?.id;
  return !!templateId && !!latestId && templateId === latestId;
});

const notificationKey = computed(() =>
  isUsingLatestInvoiceAsTemplate.value
    ? 'pages.operatingcostinvoices.latestUtilitybillExampleValues'
    : 'pages.operatingcostinvoices.otherUtilitybillExampleValues',
);

function onContractSelected(id: string | null) {
  selectedContractIdFromDetail.value = id;
  emit('contractIdChange', id);
}

async function loadContract(contractId?: string | null) {
  if (!contractId) {
    contractDto.value = null;
    contractSimple.value = null;
    isLoadingFinished.value = true;
    return;
  }

  isLoadingFinished.value = false;
  try {
    const dto = await fetchContract(contractId);
    contractDto.value = dto;
    contractSimple.value = convertContractDtoToContractSimple(dto);
  } finally {
    isLoadingFinished.value = true;
  }
}

// ----- EXISTING LIST DATA (ohne aktuell selektierte Rechnung) --------------

const existingInvoicesForList = computed<OperatingCostInvoiceSimple[]>(() => {
  if (!invoices.value?.length) return [];

  // if an invoice is currently selected, exclude it from the existing list
  if (selectedInvoiceId.value) {
    return invoices.value.filter((inv) => inv.id !== selectedInvoiceId.value);
  }

  return invoices.value;
});

watch(
  currentContractId,
  (val) => {
    void loadContract(val);
  },
  { immediate: true },
);

onMounted(async () => {
  // Fetch all invoices (can be restricted later if needed)
  importSourceInvoices.value = await fetchAllAndConvertToSimple();
});

// Convert DTOs from contract to simple list used in the table / logic
const invoices = computed<OperatingCostInvoiceSimple[]>(() =>
  convertOperatingCostInvoiceDtoListToOperatingCostInvoiceSimpleList(
    contractDto.value?.operatingCostInvoices ?? [],
  ),
);

// Wrapper so OperatingCostInvoicesDetail can use its "contracts via props" branch
const contractsProp = computed<ContractV1Dto[]>(() => {
  if (contractDto.value) {
    return [contractDto.value];
  }

  if (!contractSimple.value) return [];

  return [
    {
      id: contractSimple.value.id,
      name: contractSimple.value.name,
    } as ContractV1Dto,
  ];
});

// ----- STATE FOR DETAIL ------------------------------------------------------

const selectedInvoiceId = ref<string | null>(props.invoiceId ?? null);
const latestInvoice = ref<OperatingCostInvoiceSimple | null>(null);
const latestInvoiceLineItems = ref<OperatingCostInvoiceLineItemSimple[]>([]);
const autoPlaceholders = ref<Partial<Record<keyof OperatingCostInvoiceSimple, string>>>({});
const latestValues = ref<Partial<OperatingCostInvoiceSimple>>({});

// Which invoice is currently used as template source for the NEW invoice
const templateSourceInvoiceId = ref<string | null>(null);

const templateSourceInvoice = computed<OperatingCostInvoiceSimple | null>(() => {
  if (templateSourceInvoiceId.value) {
    const match = invoices.value.find((inv) => inv.id === templateSourceInvoiceId.value);
    if (match) return match;
  }
  // Fallback: use latest invoice as template
  return latestInvoice.value ?? null;
});

// ----- MODE (isNew / editMode) ----------------------------------------------

const wrapperIsNew = computed(() => props.isNew ?? effectiveIsNew.value);

/** Create mode if no invoice is selected (can be overridden by props.isNew) */
const effectiveIsNew = computed<boolean>(() => {
  // 1) explicit override from outside always wins
  if (props.isNew !== undefined) {
    return props.isNew;
  }

  // 2) fallback: derive from selectedInvoiceId
  return !selectedInvoiceId.value;
});

/** Effective editMode for detail component */
const effectiveEditMode = computed<boolean>(() => {
  // 1) explicit override from outside
  if (props.editMode !== undefined) {
    return props.editMode;
  }

  // 2) default: new invoices start in edit mode
  if (effectiveIsNew.value) {
    return true;
  }

  // 3) default for existing invoices
  return false;
});

// keep selectedInvoiceId in sync with external prop changes
watch(
  () => props.invoiceId,
  (val) => {
    selectedInvoiceId.value = val ?? null;
  },
);

// suggestions over all invoices of this contract
const suggestions = computed(() =>
  buildFieldSuggestions<OperatingCostInvoiceSimple>(invoices.value, {
    includeFields: [
      'issuerLegalName',
      'vatRate',
      'customerPrepaymentNet',
      'customerPrepaymentGross',
      'statedCustomerPrepaymentNet',
      'settlementResultNet',
      'settlementResultGross',
    ],
  }),
);

// initialData for "create new invoice"
const initialDataForCreate = computed<Partial<OperatingCostInvoiceSimple>>(() => {
  // base values from internal template logic (latestValues)
  const base: Partial<OperatingCostInvoiceSimple> = {
    ...latestValues.value,
    contractId: contractSimple.value?.id ?? props.contractId,
  };

  // no overrides → just use base
  if (!props.initialDataOverride) {
    return base;
  }

  // merge override on top of base (override wins)
  return {
    ...base,
    ...props.initialDataOverride,
  };
});

const placeholdersForDetail = computed<Partial<Record<keyof OperatingCostInvoiceSimple, string>>>(
  () => {
    const base = autoPlaceholders.value;

    if (!props.placeholdersOverride) {
      return base;
    }

    return {
      ...base,
      ...props.placeholdersOverride,
    };
  },
);

const latestInvoiceIdForDetail = computed(() => {
  return props.latestInvoiceIdOverride ?? latestInvoice.value?.id ?? null;
});

const latestInvoiceLineItemsForDetail = computed<OperatingCostInvoiceLineItemSimple[]>(() => {
  return props.latestInvoiceLineItemsOverride ?? latestInvoiceLineItems.value;
});

const disabledFieldsForDetail = computed<
  Partial<Record<keyof OperatingCostInvoiceSimple, boolean>>
>(() => {
  return (
    props.disabledFieldsOverride ??
    {
      // previous behavior, e.g., contractId locked etc.
    }
  );
});

// ----- HELPERS ----------------------------------------------------------------

function pickLatestInvoice(list: OperatingCostInvoiceSimple[]): OperatingCostInvoiceSimple | null {
  if (!Array.isArray(list) || list.length === 0) return null;

  return (
    [...list].sort((a, b) => {
      return Date.parse(String(b.periodStart)) - Date.parse(String(a.periodStart));
    })[0] ?? null
  );
}

async function fetchLatestInvoiceWithLines() {
  if (!latestInvoice.value?.id) {
    latestInvoiceLineItems.value = [];
    return;
  }

  const data = await fetchOperatingCostInvoice(String(latestInvoice.value.id));
  latestInvoiceLineItems.value =
    convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList(
      data.lineItems ?? [],
    );
}

function refreshTemplateFromInvoice(invoice: OperatingCostInvoiceSimple | null) {
  if (!invoice) {
    autoPlaceholders.value = {};
    latestValues.value = {};
    return;
  }

  const allPlaceholderData = {
    ...toRaw(invoice),
  };

  const { id: _id, version: _version, ...placeholderData } = allPlaceholderData;

  // 1) Determine next billing period
  const { nextStart, nextEnd } = getNextPeriod(placeholderData.periodEnd);

  // 2) Values we actually want to use as "suggestion for new billing"
  const nextDocumentReviewDeadline =
    getSameDayNextYear(String(placeholderData.documentReviewDeadline)) ?? undefined;
  const nextInternalObjectionDeadline =
    getSameDayNextYear(String(placeholderData.internalObjectionDeadline)) ?? undefined;
  const nextObjectionDeadline =
    getSameDayNextYear(String(placeholderData.objectionDeadline)) ?? undefined;

  // 3) Prefill object for initialData (will be written to the form)
  latestValues.value = {
    vatRate: placeholderData.vatRate ?? 19,
    issueDate: nextEnd,
    periodStart: nextStart,
    periodEnd: nextEnd,
    // documentReviewDeadline: nextDocumentReviewDeadline,
    // internalObjectionDeadline: nextInternalObjectionDeadline,
    // objectionDeadline: nextObjectionDeadline,
    isHeatingCostsCompliantHKVO: !!placeholderData.isHeatingCostsCompliantHKVO,
    contractId: placeholderData.contractId,
  };

  // 4) Placeholders
  const basePlaceholders = buildPlaceholders(placeholderData);

  autoPlaceholders.value = {
    ...basePlaceholders,
    issueDate: latestValues.value.issueDate ?? basePlaceholders.issueDate,
    periodStart: latestValues.value.periodStart ?? basePlaceholders.periodStart,
    periodEnd: latestValues.value.periodEnd ?? basePlaceholders.periodEnd,
    documentReviewDeadline: latestValues.value.documentReviewDeadline ?? nextDocumentReviewDeadline,
    internalObjectionDeadline:
      latestValues.value.internalObjectionDeadline ?? nextInternalObjectionDeadline,
    objectionDeadline: latestValues.value.objectionDeadline ?? nextObjectionDeadline,
  };
}

// ----- HANDLER ----------------------------------------------------------------

function openInvoicePreview(id: string) {
  // open detail in modal
  showInvoiceRef.value?.openInModal({ uuid: id });
}

function onRowClick(event: DataTableRowClickEvent) {
  const rawId = event?.data?.id;
  const id = typeof rawId === 'string' ? rawId : rawId?.meta ? rawId.meta('rawvalue') : undefined;

  if (!id) return;

  // use clicked invoice as new template source
  templateSourceInvoiceId.value = id;
  const inv = invoices.value.find((i) => i.id === id) ?? null;
  refreshTemplateFromInvoice(inv);

  // optional: still open preview modal
  if (props.existingListMode === 'modal') {
    openInvoicePreview(id);
  }

  // DO NOT set selectedInvoiceId here – we stay in "new invoice" mode
}

function onCreated(payload?: { id?: string }) {
  selectedInvoiceId.value = payload?.id ?? null;
  emit('created', { id: payload?.id });
}

function onEdited(payload?: { id?: string }) {
  emit('edited', { id: payload?.id });
}

// ----- WATCHES ----------------------------------------------------------------

watch(
  invoices,
  async (list) => {
    if (!list.length) {
      latestInvoice.value = null;
      latestInvoiceLineItems.value = [];
      autoPlaceholders.value = {};
      latestValues.value = {};
      templateSourceInvoiceId.value = null;
      return;
    }

    // always compute true "latest"
    latestInvoice.value = pickLatestInvoice(list);
    await fetchLatestInvoiceWithLines();

    // if no template source chosen yet, default to latest invoice
    if (!templateSourceInvoiceId.value && latestInvoice.value?.id) {
      templateSourceInvoiceId.value = latestInvoice.value.id;
    }

    // build placeholders and initial values from current template source
    refreshTemplateFromInvoice(templateSourceInvoice.value);
  },
  { immediate: true },
);
</script>
